import { useState, useCallback, useSyncExternalStore } from 'react';

type Equipment = 'machine' | 'dumbbell' | 'barbell' | 'bodyweight' | 'cable' | 'kettlebell' | 'bands' | 'smith_machine' | 'treadmill' | 'stationary_bike' | 'elliptical' | 'rowing_machine' | 'stair_climber' | 'ski_erg' | 'assault_bike' | 'other' | 'none' | 'full_body' | 'upper_body' | 'lower_body' | 'shoulders' | 'back' | 'chest' | 'hips' | 'legs' | 'arms' | 'core' | 'neck';

export interface ExerciseForm {
  name: string;
  equipment: Equipment;
  sets: number;
  reps: string;
  trackingType?: 'reps' | 'time' | 'cardio';
  minReps?: number;
  maxReps?: number;
  perSetTargets?: string[]; // When defined, uses individual targets per set instead of uniform reps
}

export interface SectionForm {
  title: 'Warm Up' | 'Main Workout' | 'Cooldown' | 'Accessory Work';
  duration: string;
  exercises: ExerciseForm[];
}

export interface WorkoutForm {
  name: string;
  sections: SectionForm[];
  sessionId?: string | null;
  sessionName?: string | null;
  recommendedPlaylistId?: string | null; // Energy level ID for this session
}

export interface ProgramState {
  title: string;
  description: string;
  difficulty: string;
  duration: string;
  experienceLevel: string;
  muscleGroups: string[];
  spotifyPlaylistUrl: string;
  recommendedPlaylistId: string;
  workoutCount: number;
  workouts: WorkoutForm[];
  schedule: number[];
  image: string;
}

export interface ProgramDraft {
  id: string;
  name: string;
  state: ProgramState;
  savedAt: string;
}

const DRAFTS_KEY = "programDrafts";

let listeners: (() => void)[] = [];

const emitChange = () => {
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
};

const getSnapshot = () => {
  return localStorage.getItem(DRAFTS_KEY) || "[]";
};

export const loadDrafts = (): ProgramDraft[] => {
  try {
    return JSON.parse(localStorage.getItem(DRAFTS_KEY) || "[]");
  } catch {
    return [];
  }
};

export const saveDraft = (state: ProgramState, existingDraftId?: string): string => {
  const drafts = loadDrafts();
  const draftId = existingDraftId || `draft-${Date.now()}`;
  const draftName = state.title.trim() || "Untitled Program";
  
  const newDraft: ProgramDraft = {
    id: draftId,
    name: draftName,
    state,
    savedAt: new Date().toISOString()
  };
  
  const existingIdx = drafts.findIndex(d => d.id === draftId);
  if (existingIdx >= 0) {
    drafts[existingIdx] = newDraft;
  } else {
    drafts.push(newDraft);
  }
  
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  emitChange();
  return draftId;
};

export const deleteDraft = (draftId: string): void => {
  const drafts = loadDrafts().filter(d => d.id !== draftId);
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  emitChange();
};

export function useDrafts() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  
  const drafts: ProgramDraft[] = (() => {
    try {
      return JSON.parse(snapshot);
    } catch {
      return [];
    }
  })();
  
  const removeDraft = useCallback((draftId: string) => {
    deleteDraft(draftId);
  }, []);
  
  const addOrUpdateDraft = useCallback((state: ProgramState, existingDraftId?: string) => {
    return saveDraft(state, existingDraftId);
  }, []);
  
  return {
    drafts,
    removeDraft,
    addOrUpdateDraft,
    loadDrafts,
  };
}
